'use client';
import { cn } from '@/lib/utils';
import { FC, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';

type ShowMoreTextProps = {
  children: React.ReactNode;
  limit: number;
  className?: string;
  linkColor?: string;
};

const ShowMoreText: FC<ShowMoreTextProps> = ({
  children,
  limit,
  className,
  linkColor = '#F59E0B'
}) => {
  const [showMore, setShowMore] = useState(false);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkIfClamped = () => {
      if (textRef.current) {
        const element = textRef.current;
        const isClamped = element.scrollHeight > element.clientHeight;
        setIsTextClamped(isClamped);

        // If currently clamped, mark that there is overflow
        if (isClamped) {
          setHasOverflow(true);
        }
      }
    };

    checkIfClamped();

    // Check again when content changes
    const observer = new ResizeObserver(checkIfClamped);
    if (textRef.current) {
      observer.observe(textRef.current);
    }

    return () => observer.disconnect();
  }, [children, limit]);

  return (
    <div>
      <div
        ref={textRef}
        className={cn(className, `${!showMore ? `line-clamp-${limit}` : 'line-clamp-none'}`)}>
        {children}
        <div className='line-clamp-5' />
      </div>
      {(hasOverflow || showMore) && (
        <Button
          variant='link'
          onClick={() => setShowMore(!showMore)}
          className='p-0 transition-opacity hover:opacity-80'
          style={{ color: linkColor }}>
          {showMore ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};

export default ShowMoreText;
